import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

interface FormFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  warning?: string;
  hint?: string;
  children: ReactNode;
}

/**
 * Shared label + input + error/warning/hint wrapper — 10-design-system.md §4.2, ported from
 * apps/web's `components/form/FormField.tsx` (same pattern, kept local since public-site doesn't
 * share a component bundle with the staff app). Clones the single child input/select/textarea to
 * wire `id`, `aria-invalid` and `aria-describedby` automatically.
 *
 * Error (red, blocking) and warning (amber, non-blocking — e.g. the phone-format soft check) are
 * visually and programmatically distinct per 09-landing-page.md §3.1/§3.3.
 */
export function FormField({
  id,
  label,
  required,
  error,
  warning,
  hint,
  children,
}: FormFieldProps) {
  const describedBy =
    [
      error ? `${id}-error` : null,
      !error && warning ? `${id}-warning` : null,
      hint ? `${id}-hint` : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const child = isValidElement(children) ? (children as ReactElement) : null;
  const enhanced = child
    ? cloneElement(child, {
        id,
        "aria-invalid": Boolean(error),
        "aria-describedby": describedBy,
        "aria-required": required || undefined,
        "data-warning": !error && warning ? "true" : undefined,
      })
    : children;

  return (
    <div className="fa-field">
      <label htmlFor={id}>
        {label}
        {required && (
          <span className="fa-field__required" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {enhanced}
      {error && (
        <p id={`${id}-error`} className="fa-field__error" role="alert">
          {error}
        </p>
      )}
      {!error && warning && (
        <p id={`${id}-warning`} className="fa-field__warning">
          <span aria-hidden="true">⚠</span> {warning}
        </p>
      )}
      {hint && (
        <p id={`${id}-hint`} className="fa-field__hint">
          {hint}
        </p>
      )}
    </div>
  );
}
